import { Box, Chip, TableCell, TableRow, Typography } from "@mui/material";
import moment from "moment";
import React from "react";
import TableActions from "src/common/TableActions";
import { useApiMutation } from "src/hooks/useApiMutation";
import type { IRafflePrize } from "src/Types/module/raffleDraw/raffleDraw";

interface Props {
	prize: IRafflePrize;
	serial: number;
	onEdit: (prize: IRafflePrize) => void;
}

const PrizeTableRow: React.FC<Props> = ({ prize, serial, onEdit }) => {
	const { mutate: deletePrize, isPending: isDeletePending } = useApiMutation<
		void,
		void
	>({
		method: "DELETE",
		endPoint: `/api/raffle-draw/prize/${prize.id}`,
		invalidateQueries: ["raffle-prize"],
	});

	return (
		<TableRow hover>
			<TableCell>
				<Typography variant="body2" color="text.primary" fontWeight={600}>
					{serial}
				</Typography>
			</TableCell>

			<TableCell>
				<Typography variant="body2" fontWeight={700} color="primary.main">
					{prize.prize_no || "-"}
				</Typography>
			</TableCell>

			<TableCell>
				<Typography variant="body2" color="text.secondary">
					{prize.prize_details || "-"}
				</Typography>
			</TableCell>

			<TableCell>
				<Chip
					size="small"
					label={prize.is_won ? "Won" : "Available"}
					color={prize.is_won ? "success" : "default"}
					sx={{ fontWeight: 600, fontSize: "0.75rem" }}
				/>
			</TableCell>

			<TableCell>
				{prize.is_won && prize.name ? (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Typography variant="body2" fontWeight={600}>
								{prize.name}
							</Typography>
							{prize.user_type && (
								<Chip
									size="small"
									label={prize.user_type.toUpperCase()}
									color={
										prize.user_type === "visitor"
											? "info"
											: prize.user_type === "b2b"
											? "secondary"
											: "warning"
									}
									sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
								/>
							)}
						</Box>
						<Typography variant="caption" color="text.secondary">
							Raffle: <b>{prize.raffle_no || "-"}</b>
							{prize.phone ? ` | ${prize.phone}` : ""}
						</Typography>
					</Box>
				) : (
					<Typography variant="caption" color="text.secondary">
						No Winner Yet
					</Typography>
				)}
			</TableCell>

			<TableCell>
				{prize.is_won ? (
					<Chip
						size="small"
						label={prize.prize_given === 1 ? "Yes" : "No"}
						color={prize.prize_given === 1 ? "success" : "warning"}
						sx={{ fontWeight: 600, fontSize: "0.75rem" }}
					/>
				) : (
					"-"
				)}
			</TableCell>

			<TableCell>
				<Typography variant="caption" color="text.secondary">
					{prize.created_at ? moment(prize.created_at).format("ll") : "-"}
				</Typography>
			</TableCell>

			<TableActions
				showEdit
				handleEditFun={() => onEdit(prize)}
				showDelete
				handleDeleteFun={() => deletePrize()}
				loading={isDeletePending}
			/>
		</TableRow>
	);
};

export default PrizeTableRow;
